import { lazy, type ComponentType } from "react";

/**
 * Recarrega a página quando um chunk lazy some do servidor.
 *
 * O caso é o deploy: a aba aberta ainda tem o `index.html` antigo em memória,
 * que aponta para `/assets/BackofficeQueuePage-<hash antigo>.js`. Publicado um
 * build novo, esse arquivo deixa de existir e o `import()` dinâmico estoura
 * "Failed to fetch dynamically imported module" — a tela de erro do React
 * Router que o usuário só resolve com F5.
 *
 * Aqui o F5 é automático: no primeiro erro de chunk damos `location.reload()`,
 * que baixa o HTML novo e, com ele, os hashes novos. Uma flag em
 * `sessionStorage` impede loop infinito — se o reload não resolver (chunk
 * realmente quebrado, rede caída), o erro segue para o ErrorBoundary na
 * segunda vez.
 */

const RELOAD_FLAG = "fidexa:chunk-reload";

const readFlag = () => {
  try {
    return sessionStorage.getItem(RELOAD_FLAG) !== null;
  } catch {
    // Modo anônimo / cookies bloqueados: sem flag, preferimos não recarregar
    // a arriscar loop infinito.
    return true;
  }
};

const writeFlag = (value: boolean) => {
  try {
    if (value) sessionStorage.setItem(RELOAD_FLAG, String(Date.now()));
    else sessionStorage.removeItem(RELOAD_FLAG);
  } catch {
    // sem sessionStorage não há o que marcar
  }
};

const isChunkLoadError = (error: unknown) => {
  const message = error instanceof Error ? error.message : String(error);
  return /dynamically imported module|Importing a module script failed|error loading dynamically imported module|ChunkLoadError|Loading chunk \S+ failed/i.test(
    message
  );
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any -- mesma assinatura do React.lazy
export const lazyWithReload = <T extends ComponentType<any>>(
  factory: () => Promise<{ default: T }>
) =>
  lazy(() =>
    factory()
      .then(module => {
        // Carregou: a sessão está sã de novo e um erro futuro pode recarregar.
        writeFlag(false);
        return module;
      })
      .catch((error: unknown) => {
        if (!isChunkLoadError(error) || readFlag()) throw error;

        writeFlag(true);
        window.location.reload();
        // Trava o Suspense até o reload acontecer, em vez de piscar a tela de
        // erro por uma fração de segundo.
        return new Promise<{ default: T }>(() => {});
      })
  );
